/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import axios from "axios";
import { Action, Dispatch } from "redux";

import { postsLoaded, commentsLoaded } from "../selectors/postSelector";

import {
  ADD_POST,
  ADD_COMMENT,
  DELETE_POST,
  DELETE_COMMENT,
  GET_COURSE_POSTS,
  GET_COMMENTS,
  ADD_SURVEY,
  CLEAR_SURVEY,
  UPDATE_COMMENTS,
  POST_FAILED,
  SURVEY_FAILED,
  ApricotActionCreator,
  UPDATE_POSTS,
  reqHeader,
} from "./types";

const inTest = process.env.NODE_ENV === "test";

// the 'api' passed in is the axiosInstance we created

export const addPost: ApricotActionCreator = (postInfo) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  try {
    api = api || axios;
    const { user, course, postId, content, picture, name } = postInfo;

    // if 'course' presented, add a post; otherwise, add a comment
    const props = course ? { course, content } : { postId, content };

    const { data } = await api({
      method: "post",
      url: course ? `/api/post/${user}` : `/api/post/${user}/${postId}`,
      headers: reqHeader(getState),
      data: props,
    });

    const { _id, dateAdded } = data;
    return dispatch({
      type: course ? ADD_POST : ADD_COMMENT,
      payload: { _id, dateAdded, user, content, name, picture },
    });
  } catch (err) {
    return dispatch({
      type: POST_FAILED,
      payload: null,
    });
  }
};

export const deletePost: ApricotActionCreator = (
  _id,
  isComment = false,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios;
    await api.delete(`/api/post/${_id}`);

    return dispatch({
      type: isComment ? DELETE_COMMENT : DELETE_POST,
      payload: { _id },
    });
  } catch (err) {
    return dispatch({
      type: POST_FAILED,
      payload: null,
    });
  }
};

export const getCoursePosts: ApricotActionCreator = (
  course: string,
  uid: string,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  if (!inTest && postsLoaded(getState().state_posts, { course })) {
    return Promise.resolve() as any;
  }

  try {
    api = api || axios;
    const { data } = await api.get(`/api/post/c/${course}/${uid}`);

    return dispatch({
      type: GET_COURSE_POSTS,
      payload: { course, posts: data },
    });
  } catch (err) {
    return dispatch({
      type: POST_FAILED,
      payload: null,
    });
  }
};

export const getComments: ApricotActionCreator = (pid, uid) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  if (!inTest && commentsLoaded(getState().state_posts, { pid })) {
    return Promise.resolve() as any;
  }

  try {
    api = api || axios;
    const { data } = await api.get(`/api/post/cm/${pid}/${uid}`);

    return dispatch({
      type: GET_COMMENTS,
      payload: { postId: pid, comments: data },
    });
  } catch (err) {
    return dispatch({
      type: POST_FAILED,
      payload: null,
    });
  }
};

export const updatePosts: ApricotActionCreator = (update) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: UPDATE_POSTS,
    payload: update,
  });
};

export const updateComments: ApricotActionCreator = (update) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: UPDATE_COMMENTS,
    payload: update,
  });
};

export const addSurvey: ApricotActionCreator = (props) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  try {
    api = api || axios;
    await api({
      method: "post",
      url: `/api/surv`,
      headers: reqHeader(getState),
      data: props,
    });

    return dispatch({
      type: ADD_SURVEY,
      payload: null,
    });
  } catch (err) {
    return dispatch({
      type: SURVEY_FAILED,
      payload: null,
    });
  }
};

export const clearSurvey: ApricotActionCreator = (props) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: CLEAR_SURVEY,
    payload: null,
  });
};

export type ApricotAction = ApricotActionCreator;
